import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useWorkspace } from "../../lib/WorkspaceContext";
import { formatApiErrorDetail } from "../../lib/api";
import { blockingWorkspaces, canDeleteAccount } from "../../pages/profileSections";
import { Button } from "../ui/button";

export default function DangerZoneSection() {
  const { t } = useTranslation("common");
  const { user, deleteAccount } = useAuth();
  const { workspaces, loading: workspacesLoading } = useWorkspace();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [confirmWord, setConfirmWord] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const T = (k, o) => t(`settings.profile.${k}`, o);

  if (workspacesLoading) return null;

  const blockers = blockingWorkspaces(workspaces, user?.id);
  const allowed = canDeleteAccount(workspaces, user?.id);
  const expectedWord = T("danger.confirm_word");

  const openForm = () => {
    setError("");
    setOpen(true);
  };

  const cancel = () => {
    setOpen(false);
    setError("");
    setConfirmWord("");
    setPassword("");
    setTotpCode("");
  };

  const submitDisabled =
    busy ||
    !password ||
    confirmWord.trim() !== expectedWord ||
    (user?.totp_enabled && !totpCode.trim());

  const submit = async (e) => {
    e.preventDefault();
    if (submitDisabled) return;
    setBusy(true);
    setError("");
    try {
      await deleteAccount({
        currentPassword: password,
        totpCode: user?.totp_enabled ? totpCode.trim() : undefined,
      });
      navigate("/login");
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || T("danger.error_fallback"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="border border-rose-200 rounded-2xl bg-white p-6 mb-6"
      data-testid="danger-zone-section"
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-rose-50 text-rose-700">
          <AlertTriangle size={18} strokeWidth={1.75} />
        </span>
        <div>
          <h2 className="font-display text-lg font-semibold text-zinc-950">{T("danger.title")}</h2>
          <p className="text-xs text-zinc-500 mt-0.5 max-w-md">{T("danger.subtitle")}</p>
        </div>
      </div>

      {!allowed ? (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-4"
          data-testid="danger-zone-blocked"
        >
          <p className="text-sm font-medium text-amber-900">{T("danger.blocked_title")}</p>
          <p className="text-xs text-amber-700 mt-1">{T("danger.blocked_body")}</p>
          <ul className="text-xs text-amber-800 mt-2 list-disc list-inside" data-testid="danger-zone-blocking-list">
            {blockers.map((w) => (
              <li key={w.id}>{w.name}</li>
            ))}
          </ul>
          <Link
            to="/workspaces"
            data-testid="danger-zone-blocked-manage-link"
            className="text-sm text-zinc-950 underline mt-2 inline-block"
          >
            {T("danger.blocked_manage_link")}
          </Link>
        </div>
      ) : (
        <>
          {error && (
            <div
              role="alert"
              aria-live="assertive"
              className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-3 py-2 mb-4"
              data-testid="danger-zone-error"
            >
              {error}
            </div>
          )}

          {!open ? (
            <button
              type="button"
              onClick={openForm}
              data-testid="danger-zone-open"
              className="h-10 px-4 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50"
            >
              {T("danger.open_cta")}
            </button>
          ) : (
            <form onSubmit={submit} className="space-y-3 max-w-xs">
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
                <p className="text-sm font-medium text-rose-900">{T("danger.warning_title")}</p>
                <p className="text-xs text-rose-700 mt-1">{T("danger.warning_body")}</p>
              </div>

              <div>
                <label className="text-xs text-zinc-500 block mb-1">
                  {T("danger.confirm_label")}
                </label>
                <input
                  type="text"
                  value={confirmWord}
                  onChange={(e) => setConfirmWord(e.target.value)}
                  data-testid="danger-zone-confirm"
                  className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
                />
              </div>

              <div>
                <label className="text-xs text-zinc-500 block mb-1">{T("danger.password_label")}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="danger-zone-password"
                  className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300"
                  required
                />
              </div>

              {user?.totp_enabled && (
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">{T("danger.totp_label")}</label>
                  <input
                    type="text"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    data-testid="danger-zone-totp"
                    className="w-full h-10 px-3 rounded-lg border border-zinc-200 text-sm tracking-[0.2em] focus:outline-none focus:ring-2 focus:ring-zinc-300"
                    required
                  />
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={cancel}
                  data-testid="danger-zone-cancel"
                  className="text-sm text-zinc-500 hover:text-zinc-700"
                >
                  {T("danger.cancel")}
                </button>
                <Button
                  type="submit"
                  disabled={submitDisabled}
                  data-testid="danger-zone-submit"
                  className="h-10 bg-rose-700 hover:bg-rose-800 text-white rounded-lg"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : T("danger.submit")}
                </Button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}
